import type { LocationPoint, StatusResponse, TrackingSession } from "./types";

export const substantialGapMillis = 30 * 60 * 1000;
export const currentContactMillis = 10 * 60 * 1000;
export const delayedContactMillis = 30 * 60 * 1000;

export function sortLocationsChronologically(
  locations: LocationPoint[]
): LocationPoint[] {
  return [...locations].sort((left, right) => {
    return Date.parse(left.recordedAt) - Date.parse(right.recordedAt);
  });
}

export function splitTrackByGap(
  locations: LocationPoint[],
  maxGapMillis = substantialGapMillis
): LocationPoint[][] {
  const sorted = sortLocationsChronologically(locations);
  const segments: LocationPoint[][] = [];

  for (const point of sorted) {
    const currentSegment = segments[segments.length - 1];
    if (!currentSegment || currentSegment.length === 0) {
      segments.push([point]);
      continue;
    }

    const previous = currentSegment[currentSegment.length - 1]!;
    const gap = Date.parse(point.recordedAt) - Date.parse(previous.recordedAt);
    if (gap > maxGapMillis || point.sessionId !== previous.sessionId) {
      segments.push([point]);
    } else {
      currentSegment.push(point);
    }
  }

  return segments;
}

export function isContactStale(
  latestContactAt: string | null,
  now = new Date()
): boolean {
  if (!latestContactAt) return true;
  return now.getTime() - Date.parse(latestContactAt) > delayedContactMillis;
}

export function buildStatusBanner(status: StatusResponse, now = new Date()): string {
  const message = status.quickMessage ? `${status.quickMessage} · ` : "";

  if (status.trackingActive === false) {
    return `${message}Offline`;
  }

  const contact = status.latestHeartbeatAt;
  if (!contact) return `${message}No recent contact`;

  const ageMillis = now.getTime() - Date.parse(contact);
  if (ageMillis <= delayedContactMillis) {
    return `${message}LIVE`;
  }

  return `${message}Delayed`;
}

export function ageLabel(timestamp: string | null, now = new Date()): string {
  if (!timestamp) return "No contact yet";

  const ageSeconds = Math.max(
    0,
    Math.round((now.getTime() - Date.parse(timestamp)) / 1000)
  );
  if (ageSeconds < 60) return "just now";

  const ageMinutes = Math.round(ageSeconds / 60);
  if (ageMinutes < 60) return `${ageMinutes} minute${ageMinutes === 1 ? "" : "s"} ago`;

  const ageHours = Math.round(ageMinutes / 60);
  return `${ageHours} hour${ageHours === 1 ? "" : "s"} ago`;
}

export function uploadDelayLabel(point: LocationPoint): string | null {
  const delayMillis = Date.parse(point.serverReceivedAt) - Date.parse(point.recordedAt);
  if (!Number.isFinite(delayMillis) || delayMillis < 60_000) return null;
  return durationLabel(delayMillis);
}

export function shouldShowHeadingIndicator(
  status: StatusResponse,
  point: LocationPoint | null
): boolean {
  if (!point) return false;
  if (status.trackingActive === false) return false;
  if (point.bearingDegrees === null || point.speedMps === null) return false;
  if (point.bearingDegrees < 0 || point.bearingDegrees >= 360) return false;
  return point.speedMps >= 0.5;
}

export function buildGpx(
  locations: LocationPoint[],
  sessions: TrackingSession[],
  name: string
): string {
  void sessions;
  const segments = splitTrackByGap(locations);
  const trackSegments = segments.map((segment) => {
    const points = segment.map((point) => {
      const elevation = point.altitudeMeters == null ? "" : `<ele>${point.altitudeMeters}</ele>`;
      return `<trkpt lat="${point.latitude}" lon="${point.longitude}">${elevation}<time>${escapeXml(point.recordedAt)}</time></trkpt>`;
    }).join("");
    return `<trkseg>${points}</trkseg>`;
  }).join("");

  return `<?xml version="1.0" encoding="UTF-8"?>` +
    `<gpx version="1.1" creator="Tracking App" xmlns="http://www.topografix.com/GPX/1/1">` +
    `<trk><name>${escapeXml(name)}</name>${trackSegments}</trk></gpx>`;
}

export function durationBetween(start: string, end: string | null): string {
  const endTime = end ? Date.parse(end) : Date.now();
  return durationLabel(Math.max(0, endTime - Date.parse(start)));
}

export function layoutMode(width: number): "mobile" | "desktop" {
  return width <= 860 ? "mobile" : "desktop";
}

function durationLabel(millis: number): string {
  const minutes = Math.max(1, Math.round(millis / 60000));
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  const hours = Math.round(minutes / 60);
  return `${hours} hour${hours === 1 ? "" : "s"}`;
}

function formatTimeOnly(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    timeStyle: "short"
  }).format(new Date(value));
}

function escapeXml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&apos;");
}
